import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarBlank, Clock, Coffee, FloppyDisk, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL_DAYS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const DEFAULT_DAY = { enabled: true, start: "09:00", end: "17:00", lunch_start: "13:00", lunch_end: "14:00", breaks: [
  { name: "Morning Break", duration_minutes: 15, time: "10:30" },
  { name: "Afternoon Break", duration_minutes: 15, time: "15:00" },
]};
const OFF_DAY = { enabled: false };

function emptySchedule() {
  const days = {};
  ALL_DAYS.forEach(d => { days[d.key] = d.key === "saturday" || d.key === "sunday" ? { ...OFF_DAY } : { ...DEFAULT_DAY, breaks: DEFAULT_DAY.breaks.map(b => ({...b})) }; });
  return { type: "full_time", days };
}

export default function ScheduleEditor({ userId, userName, open, onClose, onSaved }) {
  const [schedule, setSchedule] = useState(emptySchedule());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/schedules/admin/user/${userId}`).then(({ data }) => {
      if (data.configured) {
        setSchedule({ type: data.type || "full_time", days: data.days || emptySchedule().days });
        setConfigured(true);
      } else {
        setSchedule(emptySchedule());
        setConfigured(false);
      }
    }).catch(() => {
      setSchedule(emptySchedule());
      setConfigured(false);
    }).finally(() => setLoading(false));
  }, [open, userId]);

  const updateDay = (dayKey, field, value) => {
    setSchedule(prev => ({
      ...prev,
      days: { ...prev.days, [dayKey]: { ...prev.days[dayKey], [field]: value } },
    }));
  };

  const toggleDay = (dayKey) => {
    const current = schedule.days[dayKey];
    if (current?.enabled) {
      updateDay(dayKey, "enabled", false);
    } else {
      setSchedule(prev => ({
        ...prev,
        days: { ...prev.days, [dayKey]: { ...DEFAULT_DAY, breaks: DEFAULT_DAY.breaks.map(b => ({...b})) } },
      }));
    }
  };

  const updateBreak = (dayKey, breakIdx, field, value) => {
    setSchedule(prev => {
      const day = { ...prev.days[dayKey] };
      const breaks = [...(day.breaks || [])];
      breaks[breakIdx] = { ...breaks[breakIdx], [field]: value };
      return { ...prev, days: { ...prev.days, [dayKey]: { ...day, breaks } } };
    });
  };

  const addBreak = (dayKey) => {
    setSchedule(prev => {
      const day = { ...prev.days[dayKey] };
      const breaks = [...(day.breaks || []), { name: "Break", duration_minutes: 15, time: "15:00" }];
      return { ...prev, days: { ...prev.days, [dayKey]: { ...day, breaks } } };
    });
  };

  const removeBreak = (dayKey, breakIdx) => {
    setSchedule(prev => {
      const day = { ...prev.days[dayKey] };
      const breaks = [...(day.breaks || [])];
      breaks.splice(breakIdx, 1);
      return { ...prev, days: { ...prev.days, [dayKey]: { ...day, breaks } } };
    });
  };

  const applyToAllEnabled = (dayKey) => {
    const source = schedule.days[dayKey];
    if (!source?.enabled) return;
    setSchedule(prev => {
      const newDays = { ...prev.days };
      ALL_DAYS.forEach(d => {
        if (d.key !== dayKey && newDays[d.key]?.enabled) {
          newDays[d.key] = { ...source, breaks: (source.breaks || []).map(b => ({...b})) };
        }
      });
      return { ...prev, days: newDays };
    });
    toast.success("Applied to all enabled days");
  };

  const setFullTimePreset = () => setSchedule(emptySchedule());
  const setMorningsPreset = () => {
    const days = {};
    ALL_DAYS.forEach(d => {
      days[d.key] = d.key === "saturday" || d.key === "sunday"
        ? { enabled: false }
        : { enabled: true, start: "09:00", end: "13:00", lunch_start: "", lunch_end: "", breaks: [] };
    });
    setSchedule({ type: "part_time", days });
  };
  const setAfternoonsPreset = () => {
    const days = {};
    ALL_DAYS.forEach(d => {
      days[d.key] = d.key === "saturday" || d.key === "sunday"
        ? { enabled: false }
        : { enabled: true, start: "13:00", end: "17:00", lunch_start: "", lunch_end: "", breaks: [] };
    });
    setSchedule({ type: "part_time", days });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/schedules/admin/user/${userId}`, schedule);
      toast.success(`Schedule saved — ${data.weekly_hours}h/week`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save schedule");
    }
    setSaving(false);
  };

  // Calc preview hours
  const calcHours = () => {
    let totalMin = 0;
    Object.values(schedule.days).forEach(d => {
      if (!d?.enabled || !d.start || !d.end) return;
      const [sh, sm] = d.start.split(":").map(Number);
      const [eh, em] = d.end.split(":").map(Number);
      let mins = (eh * 60 + em) - (sh * 60 + sm);
      if (d.lunch_start && d.lunch_end) {
        const [lsh, lsm] = d.lunch_start.split(":").map(Number);
        const [leh, lem] = d.lunch_end.split(":").map(Number);
        mins -= (leh * 60 + lem) - (lsh * 60 + lsm);
      }
      (d.breaks || []).forEach(b => { mins -= (b.duration_minutes || 0); });
      totalMin += Math.max(mins, 0);
    });
    return (totalMin / 60).toFixed(1);
  };

  const enabledCount = Object.values(schedule.days).filter(d => d?.enabled).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F172A] flex items-center gap-2">
            <CalendarBlank size={20} weight="duotone" className="text-[#C5A059]" />
            Working Schedule — {userName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="h-40 skeleton-shimmer rounded-lg" />
        ) : (
          <div className="space-y-5">
            {!configured && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="schedule-not-configured-banner">
                Schedule not yet configured for this user. Set their working pattern below.
              </div>
            )}

            {/* Work pattern + Presets */}
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <Label className="text-xs text-[#6B7280]">Work Pattern</Label>
                <Select value={schedule.type} onValueChange={v => setSchedule(prev => ({...prev, type: v}))}>
                  <SelectTrigger className="w-[160px] h-9 border-[#E5E7EB]" data-testid="schedule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-Time</SelectItem>
                    <SelectItem value="part_time">Part-Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-1.5 pt-4">
                <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={setFullTimePreset}>Mon-Fri 9-5</Button>
                <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={setMorningsPreset}>Mornings</Button>
                <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={setAfternoonsPreset}>Afternoons</Button>
              </div>
              <div className="ml-auto pt-4 text-right">
                <p className="text-[10px] text-[#6B7280] uppercase tracking-wide">Expected Weekly Hours</p>
                <p className="text-lg font-bold text-[#0F172A]" data-testid="weekly-hours-preview">{calcHours()}h</p>
              </div>
            </div>

            {/* Day Selection Toggles */}
            <div>
              <Label className="text-xs text-[#6B7280] mb-2 block">Expected Working Days</Label>
              <div className="flex gap-2" data-testid="day-toggles">
                {ALL_DAYS.map(d => {
                  const enabled = schedule.days[d.key]?.enabled;
                  return (
                    <button
                      key={d.key}
                      onClick={() => toggleDay(d.key)}
                      className={`w-12 h-12 rounded-lg text-xs font-bold transition-all border-2 ${
                        enabled
                          ? "bg-[#0F172A] text-[#C5A059] border-[#0F172A]"
                          : "bg-white text-[#9CA3AF] border-[#E5E7EB] hover:border-[#9CA3AF]"
                      }`}
                      data-testid={`day-toggle-${d.key}`}
                    >
                      {d.label}
                      {enabled && <Check size={10} className="mx-auto mt-0.5" />}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-1">{enabledCount} day{enabledCount !== 1 ? "s" : ""} selected</p>
            </div>

            {/* Per-Day Schedule */}
            <div className="space-y-3" data-testid="day-schedules">
              {ALL_DAYS.map(d => {
                const day = schedule.days[d.key];
                if (!day?.enabled) return null;
                return (
                  <div key={d.key} className="bg-[#F9FAFB] rounded-lg p-4 border border-[#E5E7EB]" data-testid={`day-schedule-${d.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-[#0F172A] capitalize">{d.key}</h4>
                      <Button variant="ghost" size="sm" className="text-[10px] h-6 text-[#1E3A8A]" onClick={() => applyToAllEnabled(d.key)}>
                        Apply to all days
                      </Button>
                    </div>

                    {/* Working hours */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div>
                        <Label className="text-[10px] text-[#6B7280]">Start</Label>
                        <Input type="time" value={day.start || "09:00"} onChange={e => updateDay(d.key, "start", e.target.value)}
                          className="h-8 text-xs border-[#E5E7EB]" data-testid={`${d.key}-start`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#6B7280]">End</Label>
                        <Input type="time" value={day.end || "17:00"} onChange={e => updateDay(d.key, "end", e.target.value)}
                          className="h-8 text-xs border-[#E5E7EB]" data-testid={`${d.key}-end`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#6B7280]">Lunch Start</Label>
                        <Input type="time" value={day.lunch_start || ""} onChange={e => updateDay(d.key, "lunch_start", e.target.value)}
                          className="h-8 text-xs border-[#E5E7EB]" data-testid={`${d.key}-lunch-start`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#6B7280]">Lunch End</Label>
                        <Input type="time" value={day.lunch_end || ""} onChange={e => updateDay(d.key, "lunch_end", e.target.value)}
                          className="h-8 text-xs border-[#E5E7EB]" data-testid={`${d.key}-lunch-end`} />
                      </div>
                    </div>

                    {/* Breaks */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-[10px] text-[#6B7280] flex items-center gap-1"><Coffee size={10} /> Breaks</Label>
                        <Button variant="ghost" size="sm" className="text-[10px] h-5 text-[#1E3A8A]" onClick={() => addBreak(d.key)}>+ Add Break</Button>
                      </div>
                      {(day.breaks || []).map((brk, bi) => (
                        <div key={bi} className="flex items-center gap-2 mb-1">
                          <Input value={brk.name || ""} onChange={e => updateBreak(d.key, bi, "name", e.target.value)}
                            placeholder="Break name" className="h-7 text-[10px] flex-1 border-[#E5E7EB]" />
                          <Input type="time" value={brk.time || ""} onChange={e => updateBreak(d.key, bi, "time", e.target.value)}
                            className="h-7 text-[10px] w-24 border-[#E5E7EB]" />
                          <Input type="number" min={5} max={60} value={brk.duration_minutes || 15}
                            onChange={e => updateBreak(d.key, bi, "duration_minutes", parseInt(e.target.value) || 15)}
                            className="h-7 text-[10px] w-16 border-[#E5E7EB]" />
                          <span className="text-[9px] text-[#9CA3AF]">min</span>
                          <button onClick={() => removeBreak(d.key, bi)} className="text-[#9CA3AF] hover:text-[#EF4444]">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Save */}
            <div className="flex justify-end gap-3 pt-2 border-t border-[#E5E7EB]">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0F172A] gap-2" data-testid="save-schedule-btn">
                <FloppyDisk size={16} /> {saving ? "Saving..." : "Save Schedule"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
